






















































































































































import {
  DsrConstants,
  TopbarWrapper,
  SidebarOuterWrapper,
  TopbarTitle,
  SlideMenuWrapper,
  NavigationButtons,
} from '@getaccept/dsr-shared';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { ComputedRef } from '@vue/composition-api';
import { nextTick, watch, ref, computed, defineComponent, onMounted } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import { TooltipPlacement } from '@getaccept/lib-shared/src/enums/tooltip-placement';
import { AvatarSize, SlideOrigin } from '@getaccept/lib-ui';
import GetAcceptLogo from '@getaccept/lib-ui/src/assets/img/getaccept-logo-small.svg';
import ReviewChangesModal from '@getaccept/lib-shared/src/prompt-bar/ReviewChangesModal.vue';
import RevisionPromptBar from '@getaccept/lib-shared/src/prompt-bar/RevisionPromptBar.vue';
import { useLayout } from '@getaccept/dsr-shared/src/composables/layout.composable';
import { BrandingHelper } from '@getaccept/lib-shared/src/signing-theme/helpers/branding.helper';
import { AvatarHelper } from '@getaccept/lib-shared/src/helpers/avatars.helper';
import NameInput from '@getaccept/dsr-shared/src/components/NameInput.vue';
import UniversalLoader from '@getaccept/dsr-shared/src/components/UniversalLoader.vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useSessionStore } from '../../session/session.store';
import { useRevisionsStore } from '../store/revisions.store';
import ChatWrapper from '../chat/components/ChatWrapper.vue';
import EditorWrapper from '../editor-wrapper/components/EditorWrapper.vue';
import { useLoadDSR } from '../composables/load-dsr.composable';
import NotFound from '../not-found/components/NotFound.vue';
import { useParticipantsStore } from '../store/participants.store';
import { useData } from '../composables/use-data.composable';
import PageNavigationBar from './PageNavigationBar.vue';
import SlideChat from './SlideChat.vue';
import PublishedDsrSlideMenuTop from './PublishedDsrSlideMenuTop.vue';
import RecipientsOverviewModal from './RecipientsOverviewModal.vue';

export default defineComponent({
  name: 'PublishedView',
  components: {
    TopbarWrapper,
    EditorWrapper,
    PageNavigationBar,
    ChatWrapper,
    UniversalLoader,
    NotFound,
    GetAcceptLogo,
    ReviewChangesModal,
    RevisionPromptBar,
    SlideChat,
    PublishedDsrSlideMenuTop,
    RecipientsOverviewModal,
    SidebarOuterWrapper,
    TopbarTitle,
    NameInput,
    SlideMenuWrapper,
    NavigationButtons,
  },
  setup() {
    const recipientsOverviewIsVisible = ref(false);
    const participantsStore = useParticipantsStore();
    const recipient: ComputedRef<Recipient> = computed(() =>
      participantsStore.recipients.find(recipient => recipient.id === recipientId.value)
    );

    const recipients: ComputedRef<Recipient[]> = computed(() => participantsStore.recipients);
    const { showMobileView, showRightSide, showLeftSide } = useLayout();
    const showSlideMenu = ref(false);

    const dsrName = computed(() => dsr.value.name || t('untitled_room'));
    const dsrCompanyName = computed(() => dsr.value.companyName || '');
    const backgroundStyle = computed(() =>
      theme.value?.backgroundImage
        ? `background-image: url(${theme.value?.backgroundImage});`
        : `background-image: url('/assets/img/background/signing-page-bg.svg');`
    );

    const sessionStore = useSessionStore();
    const { authId, recipientId, token, documentId, recipientFirstOpen } = storeToRefs(
      sessionStore
    );

    const {
      setCurrentPage,
      currentPage,
      currentPageId,
      loading,
      dsr,
      error,
      theme,
      loadDSR,
      loadTheme,
      loadEditorImageURL,
    } = useLoadDSR();

    const {
      entity,
      recipientInput,
      updateRecipientInput,
      loading: loadingInputAndEntity,
      error: inputOrEntityError,
    } = useData();

    const revisionsStore = useRevisionsStore();
    const {
      revisions,
      showReviewChanges,
      showRevisionPromptBar,
      revisionsLoading,
      latestSealedRevision,
      forcedReviewTimer,
      loadNewContent,
    } = storeToRefs(revisionsStore);

    const dsrLogo = computed(() => BrandingHelper.getLogoUrl(theme.value));

    const visiblePages = computed(() =>
      dsr.value?.pages.filter(
        ({ editorBlock }) => !editorBlock.content.sections.every(({ hidden }) => hidden)
      )
    );

    const previousPageId = computed(() =>
      visiblePages.value.length
        ? [...visiblePages.value].reverse().find(page => page.pageNum < currentPage.value?.pageNum)
            ?.id
        : null
    );

    const nextPageId = computed(() =>
      visiblePages.value.length
        ? visiblePages.value?.find(page => page.pageNum > currentPage.value?.pageNum)?.id
        : null
    );

    const loadingDsrAndParticipants = ref(true);

    onMounted(async () => {
      await Promise.all([
        loadDSR(),
        loadTheme(documentId.value),
        participantsStore.loadRecipients(),
        participantsStore.loadUser(),
      ]);

      loadingDsrAndParticipants.value = false;
    });

    watch(
      () => loading.value,
      () => {
        if (loading.value && !dsr.value) {
          return;
        }
        revisionsStore.setRevisions(dsr.value.documentRevisions || []);
        revisionsStore.listenToNewRevisions(dsr.value.id);
      }
    );

    watch(
      () => loadNewContent.value,
      () => {
        if (!loadNewContent.value) {
          return;
        }
        loadNewContent.value = false;
        loadDSR();
      }
    );

    const navigateToSection = async ({
      selection,
      pageId,
    }: {
      selection: { sectionId: string };
      pageId: string;
    }) => {
      const sectionId = `view-${selection.sectionId}`;

      if (pageId !== currentPage.value?.id) {
        navigateToPage(pageId);
        await nextTick();
        // TODO: figure out a more reliable way the section can be scrolled to when navigating from another page
        setTimeout(() => {
          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } else {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const navigateToPage = async (pageId: string) => {
      setCurrentPage(pageId);
      await nextTick();
      document.getElementById('published-view-main-content')?.scrollTo({ top: 0 });
    };

    const senderFullName = computed(() =>
      participantsStore.user ? AvatarHelper.getDisplayName(participantsStore.user) : ''
    );

    const closeMobileMenu = () => {
      if (showSlideMenu.value) {
        showSlideMenu.value = false;
      }
    };

    watch(
      () => revisions.value,
      async () => {
        if (!latestSealedRevision.value) {
          return;
        }
        if (
          !latestSealedRevision.value.viewedByRecipient &&
          revisions.value.length !== 1 &&
          !recipientFirstOpen.value
        ) {
          if (latestSealedRevision.value.message) {
            showReviewChanges.value = true;
          }
          await revisionsStore.setRevisionViewed(latestSealedRevision.value.revision);
          await revisionsStore.loadRevisions();
        }
        sessionStore.setRecipientFirstOpen(false);
      }
    );

    return {
      loadingInputAndEntity,
      inputOrEntityError,
      loading,
      dsr,
      dsrName,
      error,
      currentPage,
      currentPageId,
      recipient,
      recipients,
      token,
      authId,
      navigateToSection,
      TooltipPlacement,
      getRecipientAvatars: AvatarHelper.getRecipientAvatars,
      AvatarSize,
      GetAcceptLogo,
      backgroundStyle,
      entity,
      recipientInput,
      senderFullName,
      showReviewChangesModal: revisionsStore.showReviewChangesModal,
      showRevisionPromptBar,
      handleReviewChanges: revisionsStore.handleReviewChanges,
      latestSealedRevision,
      showReviewChanges,
      revisionsLoading,
      forcedReviewTimer,
      SlideOrigin,
      DsrConstants,
      showMobileView,
      showRightSide,
      showLeftSide,
      showSlideMenu,
      dsrLogo,
      nextPageId,
      previousPageId,
      dsrCompanyName,
      closeMobileMenu,
      theme,
      updateRecipientInput,
      t,
      recipientsOverviewIsVisible,
      navigateToPage,
      loadingDsrAndParticipants,
      loadEditorImageURL,

      visiblePages,
    };
  },
});
